import { type ReactNode } from 'react';
import { useAuthStore } from '@/stores/authStore';
import { type AdminUserRole } from '@/types/user';

interface PermissionGateProps {
  children: ReactNode;
  permission?: string;
  permissions?: string[];
  role?: AdminUserRole | AdminUserRole[];
  organization_id?: string;
  fallback?: ReactNode;
  requireAll?: boolean; // For multiple permissions, require all vs any
}

export function PermissionGate({
  children,
  permission,
  permissions = [],
  role,
  organization_id,
  fallback = null,
  requireAll = false,
}: PermissionGateProps) {
  // Direct store access - no hook overhead
  const hasPermission = useAuthStore((state) => state.hasPermission);
  const hasRole = useAuthStore((state) => state.hasRole);
  const canAccessOrganization = useAuthStore(
    (state) => state.canAccessOrganization
  );

  // Check single permission
  if (permission && !hasPermission(permission)) {
    return <>{fallback}</>;
  }

  // Check multiple permissions
  if (permissions.length > 0) {
    const hasRequiredPermissions = requireAll
      ? permissions.every((p) => hasPermission(p))
      : permissions.some((p) => hasPermission(p));

    if (!hasRequiredPermissions) {
      return <>{fallback}</>;
    }
  }

  // Check role
  if (role && !hasRole(role)) {
    return <>{fallback}</>;
  }

  // Check organization access
  if (organization_id && !canAccessOrganization(organization_id)) {
    return <>{fallback}</>;
  }

  return <>{children}</>;
}

// Convenience components for common use cases
export function AdminOnly({
  children,
  fallback = null,
}: {
  children: ReactNode;
  fallback?: ReactNode;
}) {
  return (
    <PermissionGate role="super_admin" fallback={fallback}>
      {children}
    </PermissionGate>
  );
}

export function ManagerOnly({
  children,
  fallback = null,
}: {
  children: ReactNode;
  fallback?: ReactNode;
}) {
  return (
    <PermissionGate role={['super_admin', 'venue_manager']} fallback={fallback}>
      {children}
    </PermissionGate>
  );
}

export function AuthenticatedOnly({
  children,
  fallback = null,
}: {
  children: ReactNode;
  fallback?: ReactNode;
}) {
  const isAuthenticated = useAuthStore((state) => state.isAuthenticated);

  if (!isAuthenticated) {
    return <>{fallback}</>;
  }

  return <>{children}</>;
}
